import * as React from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence, LazyMotion } from 'motion/react';
import type { Severity } from 'types/severity';
import { Snackbar } from 'components/feedback/Snackbar';

type SnackbarRequest = {
  readonly text: string;
  readonly severity: Severity;
};

type SnackbarRequestWithKey = {
  readonly key: number;
} & SnackbarRequest;

const loadMotionFeatures = () => import('components/feedback/motionFeatures').then(mod => mod.default);

const snackbarDuration = 5000; // ms
const dangerSnackbarDuration = 8000; // ms

function getDuration(request: SnackbarRequest) {
  return request?.severity === 'danger' ? dangerSnackbarDuration : snackbarDuration;
}

function SnackbarProvider({ children }: React.PropsWithChildren<unknown>) {
  const [requests, setRequests] = React.useState<readonly SnackbarRequestWithKey[]>([]);
  const nextKeyRef = React.useRef(1);

  const showSnackbar = React.useCallback((request: SnackbarRequest) => {
    const key = nextKeyRef.current++;
    setRequests(prevRequests => [...prevRequests, { key, ...request }]);
  }, []);

  const removeSnackbar = React.useCallback((key: number) => {
    setRequests(prevRequests => prevRequests.filter(request => request.key !== key));
  }, []);

  const contextValue = React.useMemo(() => ({ showSnackbar }), [showSnackbar]);

  return (
    <SnackbarContext.Provider value={contextValue}>
      {children}
      {createPortal(
        <div className="pointer-events-none fixed inset-0 z-[9999] m-3 flex flex-col items-center justify-end gap-2 overflow-hidden">
          <LazyMotion features={loadMotionFeatures} strict>
            <AnimatePresence>
              {requests.map(({ key, ...request }) => (
                <Snackbar onClick={() => removeSnackbar(key)} key={key} duration={getDuration(request)} {...request} />
              ))}
            </AnimatePresence>
          </LazyMotion>
        </div>,
        document.body,
      )}
    </SnackbarContext.Provider>
  );
}

const SnackbarContext = React.createContext<{
  readonly showSnackbar: (request: SnackbarRequest) => void;
}>({ showSnackbar: () => void 0 });

export { SnackbarContext };
export default SnackbarProvider;
